import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isElectron } from '../auth/platform';
import { useApi } from './provider';

export const notificationKeys = {
  inbox: (unreadOnly: boolean) => ['notifications', 'inbox', unreadOnly] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,
};

interface NotificationPagePayload {
  data?: unknown[];
  meta?: { next_cursor?: string | null };
}

interface UnreadCountPayload {
  data?: { unread_count?: number };
}

/**
 * Cursor-paginated inbox feed. `unreadOnly=true` is the bell-dropdown
 * "Unread" tab; `unreadOnly=false` is the "All" tab.
 *
 * Page size 30 by default (cap 100 server-side). The bell dropdown
 * shows just the first page; the dedicated inbox page (post-v1) can
 * paginate further.
 */
export const useNotifications = (unreadOnly = false, pageSize = 30) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: notificationKeys.inbox(unreadOnly),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const query: Record<string, string> = { limit: String(pageSize) };
      if (pageParam) query.cursor = pageParam;
      if (unreadOnly) query.unread = '1';
      const { data, error } = await api.GET('/v1/notifications', {
        params: { query: query as never },
      });
      if (error) throw error;
      return data as NotificationPagePayload;
    },
    getNextPageParam: (lastPage) => lastPage.meta?.next_cursor ?? null,
  });
};

/**
 * Lightweight count poller. Calls the cheap COUNT endpoint on a 60s
 * interval — TanStack Query's default pauses background refetches
 * when the window loses focus, which is the right call on a hosted
 * web tab (don't burn server CPU on hidden tabs) but exactly the
 * wrong call inside the Electron desktop shell, where the WHOLE
 * POINT of the dock badge + native toasts is to alert the user
 * when the app isn't in focus. `isElectron()` flips
 * `refetchIntervalInBackground` per environment so both audiences
 * get the right behaviour from the same hook.
 */
export const useUnreadNotificationCount = (pollIntervalMs = 60_000) => {
  const api = useApi();
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    refetchInterval: pollIntervalMs,
    refetchIntervalInBackground: isElectron(),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/notifications/unread-count');
      if (error) throw error;
      return ((data as UnreadCountPayload | undefined)?.data?.unread_count ?? 0) as number;
    },
  });
};

export const useMarkNotificationRead = () => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.POST('/v1/notifications/{id}/read', {
        params: { path: { id } },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] }),
        qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() }),
      ]);
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/v1/notifications/read-all');
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] }),
        qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() }),
      ]);
    },
  });
};
